import { useRef, useState } from 'react';
import { useModals } from '@customerio/pluma-components/react';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { ADDON_CHANGE_FAILED_MESSAGE, AddonChangeError, type AddonSku } from '@/services/billing/addon-change';
import { WalletPurchaseInProgressError } from '@/services/billing/wallet-purchase';
import { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { persistAccountAddress } from '../utils/address-form-helpers';
import { addonSku, type AddonKey } from '../utils/addon-purchase';
import type { AddonPayment } from './use-addon-payment';
import { chooseBillingAddress, CORRECTED_ADDRESS_MESSAGE } from './use-card-entry-fields';

const ADDRESS_SAVE_FAILED_MESSAGE = "Your billing address couldn't be updated. Try again.";
const CARD_SAVE_FAILED_MESSAGE = "Your card couldn't be saved. Try again.";

export type AddonCheckoutOutcome = { payment: AddonPayment; isPurchased: boolean };

export type AddonCheckout = {
  isSubmitting: boolean;
  /** Why the last confirm stopped short, safe to show the customer. */
  error: string | undefined;
  /**
   * Resolves the payment, then buys the add-on when the variant sells one. Resolves `null` when
   * it stops short, with `error` saying why.
   */
  confirm: (key: AddonKey, resolvePayment: () => Promise<AddonPayment | null>) => Promise<AddonCheckoutOutcome | null>;
};

/** Thrown by a step to stop the checkout with a message for the customer. */
class CheckoutStop extends Error {}

/**
 * Buys an add-on from the purchase modal. The add-ons API charges the card on file, so a new card
 * is saved to the account first, along with its billing address, which the add-on's tax is
 * worked out from.
 */
export function useAddonCheckout(): AddonCheckout {
  const { account } = useAuth();
  const modals = useModals();
  const { mutateAsync: updateAccountAddress } = useUpdateAccountAddress({ accountId: account.id });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // A modal that closes mid-checkout, such as when its opener navigates away, must not go on to
  // save a card or start a charge, so each of those steps checks this first. A charge the server
  // has already started can't be stopped; cancelling the polls only stops reading its status.
  // Reset on mount, because strict mode runs the cleanup once before the modal is really closed.
  const isClosedRef = useRef(false);
  useMountEffect(() => {
    isClosedRef.current = false;
    return () => {
      isClosedRef.current = true;
      billing.pollForWalletPurchaseTask.cancelAll();
      billing.pollForAddonChangeTask.cancelAll();
    };
  });

  const saveNewCard = async (payment: Extract<AddonPayment, { method: 'new-card' }>): Promise<boolean> => {
    if (isClosedRef.current) return false;
    const address = await chooseBillingAddress(account.id, modals, payment.address);
    if (address === 'aborted') throw new CheckoutStop(CORRECTED_ADDRESS_MESSAGE);
    if (isClosedRef.current) return false;
    try {
      await persistAccountAddress(updateAccountAddress, address);
    } catch (error) {
      sentryCaptureException(error);
      throw new CheckoutStop(ADDRESS_SAVE_FAILED_MESSAGE);
    }

    if (isClosedRef.current) return false;
    const isSaved = await saveCard(payment);
    if (isSaved) void billing.initialize.perform({ refresh: true, ignoreErrors: true });
    return isSaved;
  };

  const buy = async (sku: AddonSku, payment: AddonPayment): Promise<boolean> => {
    if (payment.method === 'new-card' && !(await saveNewCard(payment))) return false;
    if (isClosedRef.current) return false;
    const runId = await billing.startAddonChange('add', [sku]);
    return (await billing.pollForAddonChangeTask.perform(runId, 'add')) === true;
  };

  const confirm: AddonCheckout['confirm'] = async (key, resolvePayment) => {
    setErrorMessage(undefined);
    setIsSubmitting(true);
    try {
      const payment = await resolvePayment();
      if (payment == null) return null;
      const sku = addonSku(key);
      if (sku == null) return { payment, isPurchased: false };
      return (await buy(sku, payment)) ? { payment, isPurchased: true } : null;
    } catch (error) {
      setErrorMessage(checkoutErrorMessage(error));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, error: errorMessage, confirm };
}

// The card-only purchase raises no invoice, so it never asks the browser to confirm a payment.
async function saveCard(payment: Extract<AddonPayment, { method: 'new-card' }>): Promise<boolean> {
  let runId: string;
  try {
    runId = await billing.startWalletPurchase({ card: { setup_intent: payment.setupIntent } });
  } catch (error) {
    if (error instanceof WalletPurchaseInProgressError) throw new CheckoutStop(error.message);
    sentryCaptureException(error);
    throw new CheckoutStop(CARD_SAVE_FAILED_MESSAGE);
  }

  try {
    const isSaved = await billing.pollForWalletPurchaseTask.perform(runId, () => {
      sentryCaptureException(new Error(`Saving a card asked to confirm a payment on run ${runId}`));
      throw new Error(CARD_SAVE_FAILED_MESSAGE);
    });
    return isSaved === true;
  } catch (error) {
    // The poll throws the words to show: a decline's own, or its failure copy.
    throw new CheckoutStop(
      error instanceof Error && !isEmptyString(error.message) ? error.message : CARD_SAVE_FAILED_MESSAGE,
    );
  }
}

function checkoutErrorMessage(error: unknown): string {
  if (error instanceof CheckoutStop || error instanceof AddonChangeError) return error.message;
  sentryCaptureException(error);
  return ADDON_CHANGE_FAILED_MESSAGE;
}
